import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/Button';

export default function Login() {
  const { user, loading, sessionExpired } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const requires2FA = params.get('requires2FA') === '1';
  const [token, setToken] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const request = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!loading && user) nav('/servers', { replace: true });
  }, [loading, user, nav]);
  useEffect(() => () => { request.current?.abort(); }, []);

  async function verify(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (request.current || pending || !/^\d{6}$/.test(token)) return;
    const controller = new AbortController();
    request.current = controller;
    setPending(true);
    setError('');
    try {
      const result = await api.postEphemeral<{ success: boolean }>('/auth/2fa/verify', { token }, controller.signal);
      if (controller.signal.aborted) return;
      if (result?.success !== true) throw new Error('Invalid MFA confirmation');
      setToken('');
      window.location.replace('/servers');
    } catch (failure) {
      if (!controller.signal.aborted) {
        const status = failure instanceof ApiError ? ` (HTTP ${failure.status})` : '';
        setError(`2FA-Bestaetigung fehlgeschlagen${status}. Code pruefen oder erneut mit Discord anmelden.`);
      }
    } finally {
      if (!controller.signal.aborted) {
        setToken('');
        setPending(false);
        request.current = null;
      }
    }
  }

  function startOAuth(): void {
    window.location.href = '/auth/discord';
  }

  return (
    <div className="min-h-full grid place-items-center px-4 py-10 relative overflow-x-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[480px] w-[480px] max-w-[140vw] rounded-full bg-accent/20 blur-3xl animate-pulseGlow"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 right-0 h-[300px] w-[300px] max-w-[100vw] rounded-full bg-accent-dim/30 blur-3xl"
      />

      <div className="relative z-10 w-full max-w-xl">
        <div className="glass rounded-2xl p-8 sm:p-10 shadow-card animate-fade-in border border-border">
          <div className="flex flex-col items-center text-center">
            <span className="v-logo text-6xl sm:text-7xl font-extrabold tracking-tighter mb-4">V</span>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">V&#8209;Bot Dashboard</h1>
            <p className="text-muted text-sm sm:text-base mb-8 max-w-xs">
              Server&#8209;Verwaltung &middot; Nitrado&#8209;Steuerung &middot; Live&#8209;Monitoring
            </p>
            {sessionExpired && (
              <div
                className="mb-5 w-full rounded-lg border border-warn/40 bg-warn/10 px-4 py-3 text-left text-sm text-warn"
                role="alert"
                data-testid="session-expired-notice"
              >
                Sitzung abgelaufen – bitte erneut anmelden.
              </div>
            )}
            {requires2FA && (
              <form onSubmit={verify} className="mb-5 w-full text-left">
                <p className="mb-3 text-sm text-muted">Discord-Anmeldung bestaetigt. Bitte den zweiten Faktor eingeben.</p>
                <label className="grid gap-2 text-sm">
                  Aktueller TOTP-Code
                  <input
                    value={token} onChange={event => setToken(event.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputMode="numeric" autoComplete="one-time-code" type="password" pattern="[0-9]{6}"
                    required disabled={pending} className="rounded-lg border border-border bg-card px-3 py-2"
                  />
                </label>
                {error && <p role="alert" className="mt-3 text-sm text-danger">{error}</p>}
                <Button type="submit" disabled={loading || pending || token.length !== 6} className="mt-3 w-full">
                  {pending ? 'Bestaetige...' : '2FA bestaetigen'}
                </Button>
              </form>
            )}
            <Button type="button" onClick={startOAuth} className="w-full" size="lg" disabled={loading || pending}>
              {loading ? 'Pruefe Session…' : 'Mit Discord anmelden'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
